import * as React from 'react';
import { Plus } from 'lucide-react';
import type { RowSelectionState } from '@tanstack/react-table';
import { Button } from '../../primitives/button.js';
import { PageHeader } from '../../primitives/page-header.js';
import { EmptyState, ErrorState, ForbiddenState, TableSkeleton } from '../../primitives/states.js';
import { TooltipProvider } from '../../primitives/tooltip.js';
import { cn } from '../../lib/cn.js';
import { useConfirm } from '../confirm.js';
import { FilterBar } from '../filters/filter-bar.js';
import { activeFilterCount, applyStateChange } from '../list-state.js';
import { useResourceList } from '../queries.js';
import { isForbidden } from '../client.js';
import type { ListState, ResourceDefinition } from '../types.js';

/**
 * The list screen never creates or edits, so it asks for the read half of a
 * definition. Any `ResourceDefinition` satisfies this, whatever its form types.
 */
export type ListResource<TItem> = Pick<
  ResourceDefinition<TItem>,
  | 'key'
  | 'label'
  | 'getRowId'
  | 'columns'
  | 'filters'
  | 'search'
  | 'rowActions'
  | 'bulkActions'
  | 'empty'
> & { client: Pick<ResourceDefinition<TItem>['client'], 'list'> };
import { BulkBar } from './bulk-bar.js';
import { DataTable } from './data-table.js';
import { PaginationBar } from './pagination-bar.js';
import { Toolbar } from './toolbar.js';
import { emitKitTelemetry, listActionOf } from '../telemetry.js';

/**
 * Overrides, layer by layer. Anything not supplied falls back to the kit's own
 * rendering, so a screen replaces the toolbar without also owning the table.
 */
export interface ResourceListOverrides<TItem> {
  header?: React.ReactNode;
  toolbarExtra?: React.ReactNode;
  empty?: React.ReactNode;
  renderTable?: (rows: readonly TItem[]) => React.ReactNode;
  renderRow?: never;
}

export interface ResourceListProps<TItem> {
  /** Only the read half of the definition is used here. */
  resource: ListResource<TItem>;
  state: ListState;
  onStateChange: (next: ListState) => void;
  /** `false` renders the forbidden state instead of querying. */
  canRead?: boolean;
  canCreate?: boolean;
  onCreate?: () => void;
  onOpen?: (row: TItem) => void;
  overrides?: ResourceListOverrides<TItem>;
  className?: string;
}

/**
 * One empty array for every render without data. A fresh `[]` each time is a
 * new dependency for the table's memoised data, so its row model was rebuilt
 * on every render while there was nothing to show.
 */
const NO_ROWS: never[] = [];

export function ResourceList<TItem>({
  resource,
  state,
  onStateChange,
  canRead = true,
  canCreate = false,
  onCreate,
  onOpen,
  overrides,
  className,
}: ResourceListProps<TItem>) {
  const [hiddenColumns, setHiddenColumns] = React.useState<ReadonlySet<string>>(
    () =>
      new Set(
        resource.columns.filter((column) => column.hiddenByDefault).map((column) => column.id),
      ),
  );
  const [selection, setSelection] = React.useState<RowSelectionState>({});
  const { confirm, dialog } = useConfirm();

  const query = useResourceList(resource, state, { enabled: canRead });

  const change = React.useCallback(
    (patch: Partial<ListState>) => {
      const next = applyStateChange(state, patch);
      const action = listActionOf(state, next);
      if (action) emitKitTelemetry({ type: 'list-action', area: resource.key, action });
      onStateChange(next);
    },
    [state, onStateChange, resource.key],
  );

  // A selection that survives a page change refers to rows the user can no
  // longer see, and a bulk action would then hit invisible records.
  React.useEffect(() => {
    setSelection({});
  }, [state.page, state.pageSize, state.sort, state.order, state.q, state.filters]);

  if (!canRead) return <ForbiddenState />;

  const rows = query.data?.items ?? NO_ROWS;
  const selectedRows = rows.filter((row) => selection[resource.getRowId(row)]);
  const bulkActions = resource.bulkActions ?? [];
  const selectable = bulkActions.length > 0;
  const hasFiltersApplied = activeFilterCount(state) > 0 || Boolean(state.q);

  const body = () => {
    if (query.isError) {
      return isForbidden(query.error) ? (
        <ForbiddenState />
      ) : (
        <ErrorState onRetry={() => void query.refetch()} />
      );
    }
    if (query.isPending) {
      return <TableSkeleton columns={resource.columns.length} />;
    }
    if (rows.length === 0) {
      if (overrides?.empty) return <>{overrides.empty}</>;
      return (
        <EmptyState
          title={
            hasFiltersApplied
              ? 'Nessun risultato'
              : (resource.empty?.title ?? `Nessun elemento in ${resource.label.many.toLowerCase()}`)
          }
          description={
            hasFiltersApplied
              ? 'Nessun record corrisponde ai filtri attivi.'
              : resource.empty?.description
          }
          action={
            hasFiltersApplied ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => change({ filters: {}, q: undefined })}
              >
                Azzera filtri
              </Button>
            ) : canCreate && onCreate ? (
              <Button variant="primary" size="sm" onClick={onCreate}>
                <Plus />
                Nuovo
              </Button>
            ) : undefined
          }
        />
      );
    }
    if (overrides?.renderTable) return <>{overrides.renderTable(rows)}</>;
    return (
      <DataTable
        columns={resource.columns}
        rows={rows}
        getRowId={resource.getRowId}
        state={state}
        onChange={change}
        hiddenColumns={hiddenColumns}
        selection={selection}
        onSelectionChange={setSelection}
        selectable={selectable}
        rowActions={resource.rowActions}
        onRowClick={onOpen}
        onConfirm={confirm}
      />
    );
  };

  return (
    <TooltipProvider>
      <section data-slot="resource-list" className={cn('flex flex-col gap-4', className)}>
        {overrides?.header ?? (
          <PageHeader
            title={resource.label.many}
            actions={
              canCreate &&
              onCreate && (
                <Button variant="primary" size="sm" onClick={onCreate}>
                  <Plus />
                  Nuovo
                </Button>
              )
            }
          />
        )}

        {selectedRows.length > 0 ? (
          <BulkBar
            rows={selectedRows}
            actions={bulkActions}
            onClear={() => setSelection({})}
            onConfirm={confirm}
          />
        ) : (
          <Toolbar
            columns={resource.columns}
            hidden={hiddenColumns}
            onToggleColumn={(id) =>
              setHiddenColumns((current) => {
                const next = new Set(current);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            state={state}
            onChange={change}
            showSearch={resource.search !== false}
            searchPlaceholder={
              resource.search === false
                ? undefined
                : (resource.search?.placeholder ?? `Cerca in ${resource.label.many.toLowerCase()}…`)
            }
            onRefresh={() => void query.refetch()}
            refreshing={query.isFetching}
          >
            {resource.filters && resource.filters.length > 0 && (
              <FilterBar filters={resource.filters} state={state} onChange={change} />
            )}
            {overrides?.toolbarExtra}
          </Toolbar>
        )}

        <div className="overflow-hidden rounded-card border border-border bg-surface">
          {body()}
          {query.data && query.data.total > 0 && (
            <PaginationBar state={state} total={query.data.total} onChange={change} />
          )}
        </div>
      </section>
      {dialog}
    </TooltipProvider>
  );
}
